import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Share2, Download, Copy, Loader2 } from "lucide-react";
import { drawSheetPages } from "@/lib/tipSheet";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const TipSheetPreview = ({ sheet }) => {
  const [pages, setPages] = useState(null);
  const id = slug(sheet.track);
  const key = JSON.stringify([sheet.status, sheet.races]);

  useEffect(() => {
    let urls = [], live = true;
    drawSheetPages(sheet).then((b) => {
      urls = b.map((x) => URL.createObjectURL(x));
      if (live) setPages({ blobs: b, urls }); else urls.forEach(URL.revokeObjectURL);
    }).catch(() => live && setPages(false));
    return () => { live = false; urls.forEach(URL.revokeObjectURL); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const files = () => pages.blobs.map((b, i) => new File([b], `weekend-edge-${id}-${sheet.date}-${i + 1}.png`, { type: "image/png" }));
  const download = () => {
    files().forEach((f) => { const u = URL.createObjectURL(f); Object.assign(document.createElement("a"), { href: u, download: f.name }).click(); URL.revokeObjectURL(u); });
    toast.success(`${pages.blobs.length} image${pages.blobs.length > 1 ? "s" : ""} saved`);
  };
  const share = async () => {
    const fs = files();
    if (!navigator.canShare?.({ files: fs })) return download();
    try { await navigator.share({ files: fs, title: `${sheet.track} tips`, text: sheet.caption || "" }); } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't share"); }
  };
  const copy = () => navigator.clipboard.writeText(sheet.caption || "").then(() => toast.success("Caption copied"), () => toast.error("Couldn't copy"));

  if (pages === false) return <p className="text-xs text-rose-300">Couldn't draw this sheet.</p>;
  if (!pages) return <p className="flex items-center gap-1.5 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" />Drawing sheet…</p>;
  return (
    <div className="space-y-3" data-testid={`tip-sheet-${id}`}>
      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1" data-testid={`tip-sheet-pages-${id}`}>
        {pages.urls.map((u, i) => <img key={u} src={u} alt={`${sheet.track} tip sheet page ${i + 1}`} className="snap-start w-[85%] sm:w-[340px] shrink-0 rounded-lg border border-white/10" data-testid={`tip-sheet-page-${id}-${i + 1}`} />)}
      </div>
      {sheet.status === "approved" && (
        <div className="flex flex-wrap gap-2">
          <button data-testid={`tip-sheet-share-${id}`} onClick={share} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-500 text-xs font-semibold"><Share2 className="w-3.5 h-3.5" />Share images</button>
          <button data-testid={`tip-sheet-download-${id}`} onClick={download} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs"><Download className="w-3.5 h-3.5" />Download {pages.blobs.length > 1 ? `${pages.blobs.length} pages` : "image"}</button>
          {sheet.caption && <button data-testid={`tip-sheet-copy-caption-${id}`} onClick={copy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs"><Copy className="w-3.5 h-3.5" />Copy caption</button>}
        </div>
      )}
    </div>
  );
};
